import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { EnvironmentVariablesService } from '@kenv';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { Constants } from '@kp/shared/constants.service';
import { UserBloc } from '@kp/user/user.bloc';
import { WorkbookBloc } from '@kp/workbook/workbook.bloc';
import { DataStoreService, OnboardingUtilities, productDisplayName, WINDOW } from '@kservice';
import { Product } from '@ktypes/enums';
import { DataStatus, Group, Status } from '@ktypes/models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-screen-mobile-app-interstitial',
  templateUrl: './screen-mobile-app-interstitial.component.html',
  styleUrls: ['./screen-mobile-app-interstitial.component.scss'],
})
export class ScreenMobileAppInterstitialComponent implements OnInit, OnDestroy {
  appDeepLink = Constants.purposefulStaticPlayStoreUrl;
  isMobile = false;
  isPurposeful = this._environmentVariablesService.product === Product.purposeful;
  productName = productDisplayName;
  showWorkbookModal = false;
  status = Status;
  validationStatus: DataStatus<Group>;
  verificationLink: string;

  private _destroy$ = new Subject<void>();
  private _groupCode: string;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _analyticsBloc: AnalyticsBloc,
    private _dataStoreService: DataStoreService,
    private _deviceDetectorService: DeviceDetectorService,
    private _environmentVariablesService: EnvironmentVariablesService,
    private _groupValidationBloc: GroupValidationBloc,
    private _onboardingBloc: OnboardingBloc,
    private _onboardingUtilities: OnboardingUtilities,
    private _userBloc: UserBloc,
    private _router: Router,
    @Inject(WINDOW) private _window: Window,
    public workbookBloc: WorkbookBloc
  ) {}

  ngOnInit() {
    this.isMobile = this._deviceDetectorService.isMobile() || this._deviceDetectorService.isTablet();

    combineLatest([
      this._groupValidationBloc.groupValidationDataStatus$,
      this._activatedRoute.paramMap != null ? this._activatedRoute.paramMap : of(null),
    ])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([validationStatus, params]: [DataStatus<Group>, ParamMap]) => {
        this.validationStatus = validationStatus;
        if (validationStatus?.status === Status.done && validationStatus?.data?.groupCode) {
          this._groupCode = validationStatus.data.groupCode;
          this.verificationLink =
            params.get('verificationLink') || this._activatedRoute?.snapshot?.queryParams?.['verificationLink'];
          this.showWorkbookModal = this._activatedRoute?.snapshot?.queryParams?.['showWorkbookModal'];
          this._createDeepLink();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  sendMobileDownloadedEvent() {
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: AnalyticsPageName.mobileAppInterstitial,
        event: AnalyticEvent.download_mobile,
      })
    );
  }

  sendContinueOnWebEvent() {
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: AnalyticsPageName.mobileAppInterstitial,
        event: AnalyticEvent.continue_on_web,
      })
    );
  }

  private _createDeepLink() {
    // if passed incorrectly, an undefined verificationLink could get stringified in the URL,
    // so ensuring that isn't used as a link
    if (this.verificationLink && this.verificationLink !== 'undefined') {
      this._onboardingBloc.setLatestPage('/welcome/notice');
      this.appDeepLink = this.verificationLink;
    } else if (this._groupCode) {
      const appLink = encodeURIComponent(
        Constants.mobileTransferDynamicLinkTemplate
          .replace('$windowLocationOrigin', this._window.location.origin)
          .replace(/\$groupCode/g, this._groupCode)
          .replace('$userId', this._dataStoreService.authData?.user?.id)
      );
      const firebaseQueryParams = Constants.mobileTransferDynamicLinkQueryParamsTemplate
        .replace('$socialTitle', `Welcome to ${productDisplayName}`)
        .replace('$socialCTA', 'Follow the link below to download the app and create your account.');
      this.appDeepLink = `${Constants.mobileTransferLinkBase}${appLink}${firebaseQueryParams}`;
    }
  }
}
